import { useCallback, useRef, useState } from 'react'
import { Device, Call } from '@twilio/voice-sdk'

// Rep-side WebRTC transport for a Copilot 1:1 call. Forked from
// ``useBargeIn`` — same ``@twilio/voice-sdk`` Device → Twilio media path that
// barge-in ships today — but for the SERVER-INITIATED rep leg:
//
//   1. The caller fetches a standalone token (GET /copilot/voice-token) and
//      registers a Device under ``client:{identity}`` — and WAITS for
//      registration to complete — BEFORE any leg is placed.
//   2. Only then does the caller POST /copilot/calls/outbound (mode="webrtc"),
//      which dials ``client:{identity}``. Registering first is load-bearing:
//      dialing the identity before the Device is registered leaves Twilio with
//      nowhere to route the leg (no-answer, 0s, prospect never bridged).
//   3. Twilio pushes the leg as an incoming call; we AUTO-ACCEPT it. The server
//      dials the WebRTC leg straight to ``/copilot/twiml/connect`` (skipping the
//      PSTN ``/twiml/bridge`` press-1 voicemail gate — a real rep is auto-
//      accepting at a laptop), so the prospect is bridged on accept with no DTMF.
//
// WebRTC here is the rep's AUDIO TRANSPORT ONLY — no conference, no live
// transcription socket, no <Start><Stream>. Post-call dual-channel extraction
// is unchanged.

export type CopilotWebrtcState =
  | 'idle'
  | 'requesting-mic'
  | 'connecting'
  | 'connected'
  | 'error'

interface ConnectArgs {
  token: string
}

interface UseCopilotWebrtcReturn {
  // Request mic permission up front, register the Device, and auto-accept the
  // incoming rep leg. Returns true once the Device is registered and waiting
  // for the incoming call; false if mic was denied / setup failed (in which
  // case the caller must NOT place the prospect-bridging POST).
  prepare: () => Promise<boolean>
  // Register the Device under the fetched token and wait for registration.
  // Returns true once the Device is registered (safe to place the leg), false
  // if registration failed (in which case the caller must NOT place the POST).
  attach: (args: ConnectArgs) => Promise<boolean>
  hangUp: () => void
  cleanup: () => void
  state: CopilotWebrtcState
  isConnected: boolean
  isConnecting: boolean
  isMuted: boolean
  toggleMute: () => void
  error: string | null
}

export function useCopilotWebrtc(): UseCopilotWebrtcReturn {
  const [state, setState] = useState<CopilotWebrtcState>('idle')
  const [isMuted, setIsMuted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const micStreamRef = useRef<MediaStream | null>(null)

  const cleanup = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    if (deviceRef.current) {
      deviceRef.current.destroy()
      deviceRef.current = null
    }
    if (micStreamRef.current) {
      // Release the mic so the browser stops showing the recording indicator
      // once the call is over.
      micStreamRef.current.getTracks().forEach((track) => track.stop())
      micStreamRef.current = null
    }
    setIsMuted(false)
  }, [])

  // Proactively acquire mic permission BEFORE the prospect is bridged. The
  // prospect call is placed by the server the moment the POST returns, so a
  // mic denial at accept-time would bridge the prospect to a mic-less rep
  // (dead air). Requesting here, before the POST, contains that failure: a
  // denial surfaces an error and the caller skips the POST entirely.
  const prepare = useCallback(async (): Promise<boolean> => {
    setError(null)
    setState('requesting-mic')
    try {
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })
      return true
    } catch {
      setError(
        'Microphone access is required to take the call in your browser. ' +
          'Allow mic access and try again, or take the call on your phone.',
      )
      setState('error')
      return false
    }
  }, [])

  // Register the Device with the fetched token and wire the auto-accept path.
  // Call this BEFORE POSTing /copilot/calls/outbound (so the Device is already
  // registered when Twilio dials ``client:{identity}``) and only when
  // ``prepare()`` resolved true. Resolves true once ``device.register()``
  // completes; false if registration failed (caller must then skip the POST).
  const attach = useCallback(
    async ({ token }: ConnectArgs): Promise<boolean> => {
      setState('connecting')
      try {
        const device = new Device(token, {
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          closeProtection: true,
        })
        deviceRef.current = device

        device.on('error', (err: { message?: string }) => {
          setError(err?.message || 'Device error')
          setState('error')
          cleanup()
        })

        // The server-initiated leg arrives as an incoming call. Auto-accept it
        // and wire the per-call lifecycle handlers.
        device.on('incoming', (call: Call) => {
          callRef.current = call

          call.on('accept', () => {
            setState('connected')
            // No DTMF here: the WebRTC rep leg skips the PSTN press-1 gate and
            // the server dials it straight to /copilot/twiml/connect, so the
            // prospect is bridged on accept. Sending digits now would only DTMF
            // the prospect.
          })

          call.on('disconnect', () => {
            setState('idle')
            cleanup()
          })

          call.on('cancel', () => {
            setState('idle')
            cleanup()
          })

          call.on('error', (err: { message?: string }) => {
            setError(err?.message || 'Call error')
            setState('error')
            cleanup()
          })

          call.accept()
        })

        await device.register()
        return true
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to connect in browser'
        setError(message)
        setState('error')
        cleanup()
        return false
      }
    },
    [cleanup],
  )

  const hangUp = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
    }
    setState('idle')
    cleanup()
  }, [cleanup])

  const toggleMute = useCallback(() => {
    if (callRef.current) {
      const newMuted = !callRef.current.isMuted()
      callRef.current.mute(newMuted)
      setIsMuted(newMuted)
    }
  }, [])

  return {
    prepare,
    attach,
    hangUp,
    cleanup,
    state,
    isConnected: state === 'connected',
    isConnecting: state === 'connecting' || state === 'requesting-mic',
    isMuted,
    toggleMute,
    error,
  }
}
